import cookie from 'js-cookie'
import request from 'sb-request'
import actions from 'redux/actions'
import { reducers, getState } from 'redux/core'
import { constants, sentry, links, array, snowplow, getQueueProductsCount, localStorage, getAbValue } from 'common/helpers'
import { getAllQueueProductsArray } from 'common/helpers/getters'
import { getFeatureToggle, getCookieValue } from 'helpers'

import { openResubscribeConfirmationModal } from 'compositions/modals/ResubscribeConfirmationModal/ResubscribeConfirmationModal'

import utils from './utils'
import api, { modifyItems } from './api'


/**
 * @param {Number} id
 * @param {Array} [months]
 * @returns {Object} item
* */
const getItemById = (id, months) => {
  const { models: { queue: { items } } } = getState()

  let item
  const itemsArray = months || items

  itemsArray.some(({ products }) => {
    item = products.find(({ id: _id }) => _id === id)

    return item
  })

  return item
}

/**
 * serverIndex is unique ACTIVE product key
 *
 * @param {Number} serverIndex - must be > -1
 * @param {Array} [months] - queue.items
 * @returns {Object} item
* */
const getItemByServerIndex = (serverIndex, months) => {
  const { models: { queue: { items } } } = getState()

  let item
  const itemsArray = months || items

  itemsArray.some(({ products }) => {
    item = products.find(({ index }) => serverIndex === index)

    return item
  })

  return item
}

/**
 * Get product by iteration index of all products
 *
 * @param {Number} index - iteration index in queue
 * @param {Array} [months] - queue.items
 * @returns {Object} product
* */
const getItemByIndex = (index, months) => {
  const { models: { queue: { items } } } = getState()

  const itemsArray = months || items
  const products = getAllQueueProductsArray(itemsArray)

  return products[index]
}

const fetch = ({ limit } = {}) => {
  const { models: { queue: { items } } } = getState()

  // if we already have loaded queue, we should reload all items
  // because some modals/actions can stop working (moveToFirstPosition)
  const minLimit = typeof limit === 'number' ? Math.max(limit, items.length) : undefined

  reducers.models.queue.setFetching(true)

  return api.fetch({ limit: minLimit })
    .then(({ items, recommendationTag }) => {
      if (recommendationTag) {
        actions.models.recommendationTag.update({ tag: recommendationTag, placement: 'queue' })
      }

      reducers.models.queue.setItems(items)

      return items
    })
}

const getLastAddedProduct = (months, position) => {
  const products = getAllQueueProductsArray(months, true)

  if (position === 'LAST') {
    return products[products.length - 1] || {}
  }

  // if position is 'FIRST' index may be not "0" due to disabled status
  return products.find(({ disabled }) => !disabled)
}

/**
 * Add 1 product to queue
 *
 * @param item {Object}
 * @param metadata {Object}
 * @param [withoutErrorHandler] {Boolean}
 * @param [force] {Boolean} force add limited item
 * @param [position] {'FIRST'|'LAST'} add to begin or end of queue
 * @param [withNotification] {Boolean} show notification
 * @returns {Promise<unknown>}
 * @private
 */
const _addItem = ({ item, metadata, withoutErrorHandler, force, position = 'LAST', withNotification = true }) => {
  const {
    models: { subscription, modals, ui },
    view: { postponedPremiumProduct },
    routing: { location: { pathname } },
  } = getState()

  const {
    closedPopups: {
      [constants.modals.premiumProducts.AddedProduct]: isPremiumProductModalHidden,
    },
  } = ui
  const { isUpgradable, isNotSubscribed, isActive, isPending, currPlan } = subscription
  const { name: planName, shippingPeriod } = currPlan

  const { id, isLimitedEdition, upchargePrice } = item

  const isWeMissedYouModalOpened = Boolean(modals[constants.modals.WeMissedYou])

  const isSubscriptionUndeliverableModalOpened  = Boolean(
    modals[constants.modals.humble.verifyShippingAddress]
    || modals[constants.modals.humble.undeliverableAddress]
  )

  const isQueueUpgradePopupEnabled = getFeatureToggle(constants.features.queueUpgradePopup)

  // TODO weird code placement to hide CountdownBanner...
  reducers.models.ui.setClosedPopup({ name: 'countdownBanner', value: true })

  // TODO this is ductape debugger to check why there is "undefined" placement in mixpanel
  if (!metadata || !metadata.placement) {
    const extra = { item, metadata, withoutErrorHandler, force }

    sentry.captureException('Add to queue placement "undefined"', { extra })
  }

  const { placement, collections, tab, page, recommenderName } = metadata || {}

  const input = {
    item: id,
    metadata: {
      placement,
      collections,
      tab,
      page,
      // ATTN we need to reset it to undefined to remove from query
      recommenderName: recommenderName || undefined,
    },
    force,
    position,
  }

  actions.models.ui.showRequestLoader()

  return api.add({ input })
    .then(({ items: months }) => {
      reducers.models.queue.setItems(months)

      reducers.models.me.increaseStatsQueueCount()

      // Block all modals if we have postponed premium product flow
      // We postponed item until payment data have been added
      if (postponedPremiumProduct.product) {
        actions.view.postponedPremiumProduct.reset()

        return months
      }

      // get 2nd month bcs 1st month may be locked with not actual plan size
      const planSize = months[1].status.length
      const {
        count: productsCount,
        activeCount: allActiveProductsCount,
      } = getQueueProductsCount(months)

      const isPaymentPage = [ links.subscribe, links.checkout ].some((link) => link.startsWith(pathname))

      /*
        priorities of modals from top to bottom
         - product with upcharge https://scentbird.atlassian.net/browse/SCNT-4928
         - LEP warning when put it not in first cell https://scentbird.atlassian.net/browse/SCNT-3764
         - upgrade subscription modal

         discussed with @olya
       */

      const isLEPPutFirstInQueueModalVisible = (
        isLimitedEdition
        && allActiveProductsCount > planSize
        && (isActive || isPending)
        && (!months[0].disabled || !months[0].skipped && !months[1].disabled)
        && shippingPeriod === 1
      )

      const isQueueUpgradeSubscriptionModalVisible = (
        isUpgradable
        && planName === constants.planNames.monthly[1]
        && isQueueUpgradePopupEnabled
        && productsCount % 6 === 0
      )

      const isAddToQueueWidgetVisible = (
        isNotSubscribed
        && !isPaymentPage
      )

      const lastAddedProduct = getLastAddedProduct(months, position)

      if (isAddToQueueWidgetVisible) {
        actions.view.queueWidget.open()
      }
      else if (upchargePrice && !isPremiumProductModalHidden) {
        actions.models.modals.open(constants.modals.premiumProducts.AddedProduct, {
          isFreeMonth: lastAddedProduct.isFree,
          item: lastAddedProduct,
          onClose: (itemWasRemoved = false) => {
            // if user didn't remove product and if limited product is added not in the first month we show popup for active users
            if (!itemWasRemoved && isLEPPutFirstInQueueModalVisible) {
              actions.models.modals.open(
                constants.modals.limitedEditionProducts.PutFirstInQueue,
                { productIndex: lastAddedProduct.index, item: lastAddedProduct }
              )
            }
          },
        })
      }
      // if limited product is added not in the first month we show popup for active users
      else if (isLEPPutFirstInQueueModalVisible) {
        actions.models.modals.open(
          constants.modals.limitedEditionProducts.PutFirstInQueue,
          { productIndex: lastAddedProduct.index, item: lastAddedProduct }
        )
      }
      else if (isQueueUpgradeSubscriptionModalVisible) {
        actions.models.modals.open(constants.modals.QueueUpgrade)
      }

      // if it's not a premium product or premium product modal was hidden by user ("don't show me this message again")
      if (withNotification && (!upchargePrice || isPremiumProductModalHidden)) {
        const lastAddedProductMonth = months.find(({ products }) => products.some(({ index }) => index === lastAddedProduct.index))
        const { month, year } = lastAddedProductMonth
        // TODO replace img to image in products item
        const { name, brand, img, image, url, upchargePrice } = item

        const notificationItem = {
          name,
          brand,
          img,
          image,
          url,
          upchargePrice,
          month,
          year,
        }

        actions.misc.notifications.show('perfumeItem', {
          item: notificationItem,
          isQueueItem: true,
          placement: metadata.placement,
        })
      }

      actions.misc.cookies.setItem(constants.cookieNames.lastTimeAddToQueue, Date.now())

      return months
    }, ({ errors }) => {
      const { error, message, additionalFields: { isSubscribedToStockNotification } } = errors[0]

      // we don't display errors in case of re-add limited product after subscription
      if (!withoutErrorHandler) {
        const limitedEditionErrorModal = ({
          lep_need_change_frequency: constants.modals.limitedEditionProducts.NeedChangeFrequency,
          lep_need_resubscribe: constants.modals.limitedEditionProducts.NeedResubscribe,
          lep_need_subscribe: constants.modals.limitedEditionProducts.NeedSubscribe,
          lep_need_unpause: constants.modals.limitedEditionProducts.NeedUnpause,
          lep_need_unskip: constants.modals.limitedEditionProducts.NeedUnskip,
        })[error]

        if (error === 'upcharge_need_payment_data') {
          actions.models.modals.open(constants.modals.premiumProducts.NeedPaymentData, { item })
        }
        else if (error === 'upcharge_need_card_update') {
          actions.models.modals.open(constants.modals.premiumProducts.NeedCardUpdate, { item })
        }
        else if (error === 'upcharge_lep_need_subscribe') {
          actions.models.modals.open(constants.modals.premiumProducts.NeedSubscription, {
            item,
            isNotSubscribed,
            link: isNotSubscribed ? links.subscribe : links.resubscribe,
          })
        }
        else if (error === 'same_perfume_added') {
          const snowplowMetadata = {
            productId: item.id,
            productName: item.name,
            productBrand: item.brand,
            productCategory: item.category,
          }

          actions.models.modals.open(constants.modals.SameFragranceError)
          snowplow.track(constants.snowplowEvents.sameProductPopup, snowplowMetadata, false)
        }
        else if (limitedEditionErrorModal) {
          // Save to add limited edition product after user action
          // set flag depending on error type
          // it's a safety net coz this flag sometimes doesn't exists.. "this has historically been the case"
          item.isLimitedEdition = true
          addItemPostponed(item, metadata, position)

          actions.models.modals.open(limitedEditionErrorModal, {
            product: { item, metadata },
            onCancel: removePostponedLimitedItem,
          })
        }
        else if (error === 'lep_need_card_update' && !isWeMissedYouModalOpened) {
          actions.models.modals.open(constants.modals.limitedEditionProducts.NeedUpdateCard)
        }
        else if (error === 'lep_need_address_update' && !isSubscriptionUndeliverableModalOpened) {
          actions.models.modals.open(constants.modals.limitedEditionProducts.NeedUpdateAddress)
        }
        else if (error === 'lep_out_of_stock') {
          actions.models.modals.open(constants.modals.limitedEditionProducts.OutOfStockNotify, {
            productId: id,
            isSubscribedToStockNotification,
          })
        }
        else if (message) {
          actions.models.modals.open(constants.modals.ServerError, {
            content: message,
          })
        }
      }
    })
    .finally(() => {
      actions.models.ui.hideRequestLoader()
    })
}

/**
 *
 * @param {Array} [items]
 * @param {Object} [metadata]
 * @param {Object} [options]
 * @param {Boolean} [options.isNotificationVisible] - show notification after action
 * @param {Boolean} [options.withoutErrorHandler] - ignore errors
 * @param {Boolean} [options.postponeLimitedItems] - postpone limited items with error for future request
 * @param {'FIRST'|'LAST'} [options.position] - add to begin or end of queue
 * @returns {Promise.<any>}
 * @private
 */
const _addItems = (items, metadata, options = {}) => {
  const { isNotificationVisible, withoutErrorHandler, postponeLimitedItems, position } = options
  const { models: { modals } } = getState()

  const isWeMissedYouModalOpened = Boolean(modals[constants.modals.WeMissedYou])

  const isSubscriptionUndeliverableModalOpened  = Boolean(
    modals[constants.modals.humble.verifyShippingAddress]
    || modals[constants.modals.humble.undeliverableAddress]
  )

  const filteredItems = items
    ? items.filter((item) => item)
    : []

  if (!filteredItems.length) {
    sentry.captureException(`_addItems with empty array`, {
      extra: {
        items,
        metadata,
        options,
      },
    })

    return Promise.resolve()
  }

  // TODO this is ductape debugger to check why there is "undefined" placement in mixpanel
  if (!metadata || !metadata.placement) {
    const extra = { items, metadata, options }

    sentry.captureException('Add to queue items placement "undefined"', { extra })
  }

  const { placement, collections, tab, page, recommenderName } = metadata || {}

  const input = {
    items: filteredItems,
    metadata: {
      placement,
      collections,
      tab,
      page,
      recommenderName,
    },
    position: position || 'LAST',
  }

  return api.add({ input })
    .then(
      ({ items: months, productsAdded }) => {
        reducers.models.queue.setItems(months)
        reducers.models.me.increaseStatsQueueCount(productsAdded)

        if (isNotificationVisible && productsAdded > 0) {
          actions.misc.notifications.show('severalQueueItems', { itemsCount: productsAdded })
        }

        return months
      },
      ({ errors, resData: { items: months, productsAdded } }) => {
        reducers.models.queue.setItems(months || [])
        reducers.models.me.increaseStatsQueueCount(productsAdded)

        if (isNotificationVisible && productsAdded > 0) {
          actions.misc.notifications.show('severalQueueItems', { itemsCount: productsAdded })
        }

        // if request is terminated then "errors" will be undefined
        if (errors) {
          // if it's a first attempt to add items and we got LEP errors
          // we should try to add LEP items later

          // Filter LEP items by error for adding after subscription
          const lepErrors = [
            'lep_need_change_frequency',
            'lep_need_resubscribe',
            'lep_need_subscribe',
            'lep_need_unpause',
            'lep_need_unskip',
            'lep_need_card_update',
            'lep_need_address_update',
            'upcharge_lep_need_subscribe',
          ]

          let firstError = null

          const failedItems = errors
            .filter(({ error }) => lepErrors.includes(error))
            .map(({ additionalFields: { id }, error }) => {
              if (!firstError) {
                firstError = error
              }

              return id
            })

          if (failedItems.length) {
            // If it's a postpone request we should postpone items for future and don't through an error
            if (postponeLimitedItems) {
              // Save items to add after registration / authorization
              _addLimitedItemsPostponed(failedItems, metadata, { isNotificationVisible })
            }
            else if (!withoutErrorHandler) {
              const limitedEditionErrorModal = ({
                lep_need_change_frequency: constants.modals.limitedEditionProducts.NeedChangeFrequency,
                lep_need_resubscribe: constants.modals.limitedEditionProducts.NeedResubscribe,
                lep_need_subscribe: constants.modals.limitedEditionProducts.NeedSubscribe,
                lep_need_unpause: constants.modals.limitedEditionProducts.NeedUnpause,
                lep_need_unskip: constants.modals.limitedEditionProducts.NeedUnskip,
              })[firstError]

              if (limitedEditionErrorModal) {
                // Last chance for user
                _addLimitedItemsPostponed(failedItems, metadata, { isNotificationVisible })

                actions.models.modals.open(limitedEditionErrorModal, {
                  onCancel: removePostponedLimitedItems,
                })
              }
              else if (firstError === 'lep_need_card_update' && !isWeMissedYouModalOpened) {
                actions.models.modals.open(constants.modals.limitedEditionProducts.NeedUpdateCard)
              }
              else if (firstError === 'lep_need_address_update' && !isSubscriptionUndeliverableModalOpened) {
                actions.models.modals.open(constants.modals.limitedEditionProducts.NeedUpdateAddress)
              }
            }
          }
        }

        return months || []
      }
    )
}

// Save and add item after registration / authorization
const addItemPostponed = (item, metadata, position) => {
  if (item.isLimitedEdition) {
    localStorage.setItem(constants.localStorageNames.queueLimitedProduct, { item, metadata, position })
  }
  else {
    localStorage.setItem(constants.localStorageNames.queueProduct, { item, metadata, position })
  }

  return Promise.resolve()
}

const _addItemsPostponed = (items, metadata, options) => {
  localStorage.setItem(constants.localStorageNames.queueProducts, { items, metadata, options })
  // window.location.href for reloading page if redirect from SEO page
  actions.models.router.push(links.register, true, true)
}

const _addLimitedItemsPostponed = (items, metadata, options) => {
  localStorage.setItem(constants.localStorageNames.queueLimitedProducts, { items, metadata, options })
}

/**
 * Add 1 item to queue
 *
 * @param item {Object}
 * @param metadata {Object}
 * @param metadata.placement {String}
 * @param [metadata.page] {String}
 * @param [position] {'FIRST'|'LAST'}
 * @param [withNotification] {Boolean} - show noification
 * @param [isResubscribeFlowApplicable] {Boolean} - resubscribe confirmation flow
 * @returns {Promise<unknown>|*}
 */
const addItem = (item, metadata, position = 'LAST', withNotification = true, isResubscribeFlowApplicable = true) => {
  const {
    models: {
      me: { isLoggedIn, firstSubscriptionDate },
      subscription: { isCancelled, subscriptionDate },
    },
  } = getState()

  let { isLimitedEdition } = item
  // TODO remove this when backend will return isLimitedEdition
  if (typeof isLimitedEdition !== 'boolean' && item.labels) {
    item.isLimitedEdition = item.labels.includes('limited-edition')
  }

  const isMobile = window.__globals__.device.isMobile
  const isNewResubscribeFlowEnabled = getAbValue(constants.abTests.resubscribeFlow) === 'b'

  const isFirstTimeResubscribeFlow = (
    isMobile
    && isResubscribeFlowApplicable
    && isNewResubscribeFlowEnabled
    && isCancelled
    && firstSubscriptionDate === subscriptionDate // first-time resubscribe only
  )

  const isSecondTimeResubscribeFlow = (
    isCancelled
    && firstSubscriptionDate !== subscriptionDate // SB-1914
  )

  const isNewResubscribeFlow = isFirstTimeResubscribeFlow || isSecondTimeResubscribeFlow

  if (isNewResubscribeFlow) {
    const { placement } = metadata || {}

    openResubscribeConfirmationModal({
      resubscribeCode: constants.resubscribeTypes.resubfreeproduct, // SB-1820
      productToAdd: item,
      metadata,
      placement,
    })

    return Promise.reject()
  }

  if (isLoggedIn) {
    return _addItem({ item, metadata, position, withNotification })
  }

  return addItemPostponed(item, metadata, position)
    .then(() => actions.models.router.push(links.register, true))
}

const addItems = (items, metadata, isNotificationVisible) => {
  const { models: { me: { isLoggedIn } } } = getState()

  if (isLoggedIn) {
    return _addItems(items, metadata, { isNotificationVisible })
  }

  return _addItemsPostponed(items, metadata, { isNotificationVisible })
}

const removePostponedLimitedItem = () =>
  localStorage.removeItem(constants.localStorageNames.queueLimitedProduct)

const removePostponedLimitedItems = () =>
  localStorage.removeItem(constants.localStorageNames.queueLimitedProducts)

const removeAllPostponedLimitedItems = () => {
  removePostponedLimitedItem()
  removePostponedLimitedItems()
}

/**
 *
 * @param {string} localStorageName
 * @param {Boolean} withoutErrorHandler
 * @param {Boolean} force
 * @returns {Promise<unknown>}
 * @private
 */
const _resolvePostponedItem = (localStorageName, withoutErrorHandler, force) => {
  const { item, metadata, position } = localStorage.getItem(localStorageName, {})

  localStorage.removeItem(localStorageName)

  if (item) {
    return _addItem({ item, metadata, withoutErrorHandler, position, force })
  }

  return Promise.resolve()
}

const resolvePostponedItem = () => (
  _resolvePostponedItem(constants.localStorageNames.queueProduct)
)

/**
 *
 * @param {Object} [data]
 * @param {Boolean} [data.withoutErrorHandler] - do not show popups in case of error
 * @param {Boolean} [data.force] - add LEP to queue in case when 1st month is skipped
 */
const resolvePostponedLimitedItem = ({ withoutErrorHandler, force } = {}) => (
  _resolvePostponedItem(constants.localStorageNames.queueLimitedProduct, withoutErrorHandler, force)
)

const resolvePostponedItems = () => {
  const postponedQueueProducts = localStorage.getItem(constants.localStorageNames.queueProducts, {})
  const { items, metadata, options } = postponedQueueProducts

  localStorage.removeItem(constants.localStorageNames.queueProducts)

  if (items && items.length) {
    return _addItems(
      items,
      metadata,
      { ...options, withoutErrorHandler: true, postponeLimitedItems: true }
    )
  }

  return Promise.resolve()
}

/**
 *
 * @param {Object} [data]
 * @param {Boolean} [data.withoutErrorHandler] - do not show popups in case of error
 * @param {Boolean} [data.force] - add LEP to queue in case when 1st month is skipped
 */
const resolvePostponedLimitedItems = ({ withoutErrorHandler, force } = {}) => {
  const postponedQueueProducts = localStorage.getItem(constants.localStorageNames.queueLimitedProducts, {})
  const { items, metadata, options } = postponedQueueProducts

  localStorage.removeItem(constants.localStorageNames.queueLimitedProducts)

  if (items && items.length) {
    return _addItems(
      items,
      metadata,
      { ...options, withoutErrorHandler, force }
    )
  }

  return Promise.resolve()
}

const resolvePickedProducts = ({ isNotificationVisible } = {}) => {
  const { models: { subscription: { isSubscribed } } } = getState()

  const { items = [], metadata } = localStorage.getItem(constants.localStorageNames.pickedProducts) || {}

  let selectedItems = items.filter((item) => item && item.id)

  if (selectedItems.length) {
    const isPremiumProductsForNotSubscribedEnabled = getFeatureToggle(constants.features.premiumProductsForNotSubscribed)

    // We can't add a LEP product to the first position in the queue for not subscribed user.
    // This case active if user left payment flow
    if (!isSubscribed) {
      const isFirstLepProduct = selectedItems[0].isLimitedEdition
      const areAllProductsLep = selectedItems.every(({ isLimitedEdition }) => isLimitedEdition)

      // Block cases: [ LEP ] or [ LEP, LEP, LEP ... ]
      // Need delete this products, because bugs are possible after subscribing user in future
      if (areAllProductsLep) {
        localStorage.removeItem(constants.localStorageNames.pickedProducts)

        return
      }

      // Find first not LEP product, and swap it
      if (isFirstLepProduct) {
        const firstNotLepProductIndex = selectedItems.findIndex(({ isLimitedEdition }) => !isLimitedEdition)
        selectedItems = array.moveTo(selectedItems, firstNotLepProductIndex, 0)
      }
    }

    const pickedItems = selectedItems.map((item) => item.id)
    // premiumProductsForNotSubscribed added items to pick page store from sessionStorage (not localStorage)
    // Need clear store here only for localStorage flow.
    // premiumProductsForNotSubscribed will be deleted in payment flow (buySubscription)
    if (!isPremiumProductsForNotSubscribedEnabled) {
      actions.view.pickPage.clearSelectedItems()
    }

    localStorage.removeItem(constants.localStorageNames.pickedProducts)

    return _addItems(
      pickedItems,
      metadata,
      { postponeLimitedItems: true, isNotificationVisible, position: 'FIRST' }
    )
  }

  return Promise.resolve()
}

const resolveSkincareFlowItems = ({ isNotificationVisible } = {}) => {
  const { view: { skincareIntroPage: { selectedItems } } } = getState()

  actions.view.skincareIntroPage.clearSelectedItems()

  if (selectedItems.length) {
    const skincareIntroSelectedItems = selectedItems.map((item) => typeof item === 'number' ? item : item.id)
      .filter((id) => Boolean(id))

    return _addItems(
      skincareIntroSelectedItems,
      { placement: 'skincare intro page' },
      { postponeLimitedItems: true, isNotificationVisible }
    )
  }

  return Promise.resolve()
}

const resolveCollectionLandingItems = ({ isNotificationVisible } = {}) => {
  const { view: { collectionLandingPage: { products } } } = getState()

  actions.view.collectionLandingPage.clearProducts()

  const selectedItems = products
    ? products.filter(({ isSelected }) => isSelected).map(({ id }) => id)
    : []

  if (selectedItems.length) {
    return _addItems(
      selectedItems,
      { placement: 'landing collection page' },
      { postponeLimitedItems: true, isNotificationVisible }
    )
  }

  return Promise.resolve()
}

const resolveMakeupLandingItems = ({ isNotificationVisible } = {}) => {
  const { view: { makeupLandingPage: { selectedItems } } } = getState()

  actions.view.makeupLandingPage.clearSelectedItems()

  if (selectedItems && selectedItems.length) {
    return _addItems(
      selectedItems,
      { placement: 'makeup landing page' },
      { postponeLimitedItems: true, isNotificationVisible }
    )
  }

  return Promise.resolve()
}

const resolveMakeupLandingNewStyleItems = ({ isNotificationVisible } = {}) => {
  const { view: { makeupLandingPage: { selectedLandingItems } } } = getState()

  actions.view.makeupLandingPage.clearSelectedLandingItems()

  if (selectedLandingItems.length) {
    const makeupLandingNewStyleSelectedItems = selectedLandingItems.map(({ id }) => id)
      .filter((id) => Boolean(id))

    return _addItems(
      makeupLandingNewStyleSelectedItems,
      { placement: 'main makeup landing pick' },
      { postponeLimitedItems: true, isNotificationVisible }
    )
  }

  return Promise.resolve()
}

// Resolving items after login
const resolveAllPostponedItems = async ({ isNotificationVisible } = {}) => {
  try {
    await resolvePostponedItem()
    await resolvePostponedItems()
    await resolvePickedProducts({ isNotificationVisible })
    await resolveCollectionLandingItems({ isNotificationVisible })
    await resolveMakeupLandingItems({ isNotificationVisible })
    await resolveMakeupLandingNewStyleItems({ isNotificationVisible })
    await resolveSkincareFlowItems({ isNotificationVisible })
  }
  catch (err) {
    let error = (err && err.stack) ? err.stack : err
    if (typeof error !== 'string') {
      error = JSON.stringify(error)
    }
    sentry.captureException('resolveAllPostponedItems exception', {
      extra: {
        error,
      },
    })
  }
}

// Resolving limited items
const resolveAllPostponedLimitedItems = async ({ withoutErrorHandler = false, force = false } = {}) => {
  try {
    await resolvePostponedLimitedItem({ withoutErrorHandler, force })
    await resolvePostponedLimitedItems({ withoutErrorHandler, force })
  }
  catch (err) {
    sentry.captureException(`resolveAllPostponedLimitedItems exception: ${error.toString()}`, {
      extra: {
        stack: err.stack,
      },
    })
  }
}

const _extendNewMonths = (months, products) => {
  let interval = 0

  return months.map((month) => {
    const productCount = month.products.length
    const productsChunk = products.slice(interval, interval + productCount)

    interval += productCount

    return {
      ...month,
      products: productsChunk,
    }
  })
}

export const moveProduct = (months, oldIndex, newIndex, checkDisabled) => {
  let products = getAllQueueProductsArray(months)

  products = utils.move(products, oldIndex, newIndex, checkDisabled)

  return _extendNewMonths(months, products)
}

/**
 * Remove product by serverIndex
 *
 * @param {Number} months
 * @param {Number} serverIndex
 * @param {Function} checkDisabled
 * @returns {Array} months
 */
export const removeProduct = (months, serverIndex, checkDisabled) => {
  let products = getAllQueueProductsArray(months)
  const itemIndex = products.findIndex(({ index }) => index === serverIndex)

  products[itemIndex] = { isEmpty: true, disabled: true }

  products = utils.remove(products, itemIndex, checkDisabled)

  cookie.remove(constants.cookieNames.buyQueuePageData)

  return _extendNewMonths(months, products)
}

/**
 *
 * @param {Object} params
 * @param {number} params.oldIndex
 * @param {number} params.newIndex
 * @param {Object} params.metadata
 * @param {string} params.metadata.placement
 * @param {number} params.metadata.id
 * @param {string} params.metadata.name
 * @param {string} params.metadata.brand
 */
const moveItem = ({ oldIndex, newIndex, metadata = {} }) => {
  if (oldIndex === newIndex) {
    return
  }

  const { models: { queue: { items: months }, subscription: { currPlan: { shippingPeriod } } } } = getState()
  const { id, index: oldServerIndex, isLimitedEdition, isUnavailable: wasUnavailable } = getItemByIndex(oldIndex)
  const { index: newServerIndex } = getItemByIndex(newIndex)

  // get 2nd month bcs 1st month may be locked with not actual plan size
  const planSize = months[1].status.length
  const newItems = moveProduct(months, oldIndex, newIndex, ({ disabled }) => disabled)

  const data = {
    item: id,
    indexFrom: oldServerIndex,
    indexTo: newServerIndex,
    page: metadata.placement,
  }

  const snowplowMetadata = {
    indexFrom: oldServerIndex,
    indexTo: newServerIndex,
    productName: metadata.name,
    productBrand: metadata.brand,
    productId: metadata.id,
  }

  reducers.models.queue.setItems(newItems)

  snowplow.track(constants.snowplowEvents.queueProductMove, snowplowMetadata, false)

  actions.models.ui.showRequestLoader()

  return request.put('rest/subscription/queue/items', {
    body: data,
    modifyResult: modifyItems,
    withErrorModal: false,
  })
    .then(({ items: months }) => {
      reducers.models.queue.setItems(months)

      const item = getItemByServerIndex(newServerIndex, months)
      const { isUnavailable } = item

      const isLEPMovedBackward = (
        isLimitedEdition
        && oldServerIndex < planSize
        && newServerIndex >= planSize
        && (!months[0].disabled || !months[0].skipped && !months[1].disabled)
      )
      const isPutFirstInQueueModalVisible = (
        isLEPMovedBackward
        && !wasUnavailable
        && !isUnavailable
        && !getCookieValue(constants.cookieNames.isPutFirstInQueueModalShown)
        && shippingPeriod === 1
      )

      if (isPutFirstInQueueModalVisible) {
        actions.models.modals.open(
          constants.modals.limitedEditionProducts.PutFirstInQueue,
          { productIndex: newServerIndex, item }
        )
        actions.misc.cookies.setSessionItem(constants.cookieNames.isPutFirstInQueueModalShown, true)
      }
    }, ({ error, title, statusCode, message, resData: { items } }) => {
      const { items: months } = modifyItems({ items })

      reducers.models.queue.setItems(months)

      const limitedEditionErrorModals = {
        lep_need_change_frequency: constants.modals.limitedEditionProducts.NeedChangeFrequency,
        lep_need_resubscribe: constants.modals.limitedEditionProducts.NeedResubscribe,
        lep_need_subscribe: constants.modals.limitedEditionProducts.NeedSubscribe,
        lep_need_unpause: constants.modals.limitedEditionProducts.NeedUnpause,
        lep_need_unskip: constants.modals.limitedEditionProducts.NeedUnskip,
        lep_need_card_update: constants.modals.limitedEditionProducts.NeedUpdateCard,
        lep_need_address_update: constants.modals.limitedEditionProducts.NeedUpdateAddress,
      }

      if (Object.keys(limitedEditionErrorModals).includes(error)) {
        actions.models.modals.open(limitedEditionErrorModals[error], { isMoveAction: true })
      }
      else if (error === 'lep_impossible_put_first_in_queue') {
        actions.models.modals.open(constants.modals.limitedEditionProducts.OutOfStock, { cantMoveInQueue: true })
      }
      else if (message) {
        actions.models.modals.open(constants.modals.ServerError, {
          title,
          content: message,
          statusCode,
        })
      }
      else {
        actions.models.modals.open(constants.modals.SomethingWentWrong, {
          endpoint: 'rest/subscription/queue/items',
          body: data,
          statusCode,
          error,
          title,
          message,
        })
      }
    })
    .finally(() => {
      actions.models.ui.hideRequestLoader()
    })
}

const moveToFirstPosition = (serverIndex, metadata) => {
  const { models: { queue: { items: months } } } = getState()

  const allProducts = getAllQueueProductsArray(months)
  const oldIndex = allProducts.findIndex((item) => item && (item.index === serverIndex))
  const newIndex = allProducts.findIndex((item) => item && (item.index === 0))

  if (oldIndex === -1 || newIndex === -1) {
    sentry.captureException(`moveToFirstPosition: items not found`, {
      extra: {
        serverIndex,
        metadata,
        oldIndex,
        newIndex,
        allProducts,
        months,
      },
    })
    return
  }

  moveItem({ oldIndex, newIndex, metadata })
}

/**
 *
 * @param {Object} params
 * @param {number} params.id
 * @param {number} [params.index] - serverIndex!
 * @param {string} params.page
 * @param {Object} params.metadata
 * @param {string} params.metadata.placement
 * @param {string} params.metadata.name
 * @param {string} params.metadata.brand
 */
const removeItem = ({ id: itemId, index, page, metadata = {} }) => {
  const { models: { queue: { items: months } } } = getState()

  let serverIndex = index

  // If you do not pass the index, the first product in the queue will be deleted
  if (typeof serverIndex !== 'number') {
    const { index } = getItemById(itemId, months) || {}
    serverIndex     = index
  }

  const newItems = removeProduct(months, serverIndex, ({ disabled }) => disabled)

  const data = {
    page,
    metadata,
    item: itemId,
    index: serverIndex,
  }

  const snowplowMetadata = {
    placement: metadata.placement,
    productName: metadata.name,
    productBrand: metadata.brand,
    productId: itemId,
  }

  reducers.models.queue.setItems(newItems)
  reducers.models.me.decreaseStatsQueueCount()

  snowplow.track(constants.snowplowEvents.queueProductRemove, snowplowMetadata, false)

  actions.models.ui.showRequestLoader()

  return request.post('rest/subscription/queue/items/delete', {
    body: data,
    modifyResult: modifyItems,
  })
    .then(({ items: months }) => {
      reducers.models.queue.setItems(months)

      return months
    }, ({ error, title, statusCode, message, resData: { items } }) => {
      const { items: months } = modifyItems({ items })

      reducers.models.queue.setItems(months)
      reducers.models.me.increaseStatsQueueCount()
      actions.models.modals.open(constants.modals.SomethingWentWrong, {
        endpoint: 'rest/subscription/queue/items/delete',
        body: data,
        statusCode,
        message,
        error,
        title,
      })
    })
    .finally(() => {
      actions.models.ui.hideRequestLoader()
    })
}

const clear = () =>
  request.delete('rest/subscription/queue')


export default {
  fetch,
  addItem,
  addItemPostponed,
  addItems,
  resolveAllPostponedItems,
  resolveAllPostponedLimitedItems,
  removeAllPostponedLimitedItems,
  moveItem,
  moveToFirstPosition,
  removeItem,
  clear,
}

export {
  modifyItems,
}
